import { computed, ref, unref, watch } from 'vue';

// Só uma conversa fica com as ações abertas por vez em toda a lista.
const openSwipeId = ref(null);

const AXIS_LOCK_DISTANCE = 8;
const OPEN_RATIO = 0.4;
const OVERSHOOT = 24;

export const closeAllConversationSwipes = () => {
  openSwipeId.value = null;
};

/**
 * Deslizar o cartão para a esquerda (toque) revela as ações da conversa,
 * como no WhatsApp. Gestos verticais seguem rolando a lista.
 *
 * @param {Object} options
 * @param {import('vue').Ref|number} options.id - conversa da linha
 * @param {import('vue').Ref|number} options.actionsWidth - largura das ações em px
 * @param {import('vue').Ref|boolean} options.enabled - gesto disponível
 */
export function useConversationSwipe({ id, actionsWidth, enabled = true }) {
  const offset = ref(0);
  const dragging = ref(false);
  let startX = 0;
  let startY = 0;
  let startOffset = 0;
  let axis = null;
  let suppressNextClick = false;

  const isOpen = computed(() => openSwipeId.value === unref(id));

  const close = () => {
    offset.value = 0;
    if (isOpen.value) openSwipeId.value = null;
  };

  const open = () => {
    offset.value = -unref(actionsWidth);
    openSwipeId.value = unref(id);
  };

  watch(openSwipeId, value => {
    if (value !== unref(id) && !dragging.value) offset.value = 0;
  });

  watch(
    () => unref(id),
    () => {
      offset.value = 0;
      dragging.value = false;
      axis = null;
    }
  );

  const onTouchStart = event => {
    suppressNextClick = false;
    if (!unref(enabled) || event.touches.length !== 1) return;
    // Tocar em outra conversa só fecha a que estava aberta, sem abrir esta.
    if (openSwipeId.value !== null && !isOpen.value) {
      closeAllConversationSwipes();
      suppressNextClick = true;
    }
    const touch = event.touches[0];
    startX = touch.clientX;
    startY = touch.clientY;
    startOffset = offset.value;
    axis = null;
  };

  const onTouchMove = event => {
    if (!unref(enabled) || event.touches.length !== 1) return;
    const touch = event.touches[0];
    const dx = touch.clientX - startX;
    const dy = touch.clientY - startY;

    if (!axis) {
      if (
        Math.abs(dx) < AXIS_LOCK_DISTANCE &&
        Math.abs(dy) < AXIS_LOCK_DISTANCE
      ) {
        return;
      }
      axis = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y';
      if (axis === 'x') {
        dragging.value = true;
        openSwipeId.value = unref(id);
      } else if (openSwipeId.value !== null) {
        // Rolar a lista recolhe as ações abertas.
        closeAllConversationSwipes();
      }
    }
    if (axis !== 'x') return;

    const width = unref(actionsWidth);
    offset.value = Math.min(0, Math.max(-width - OVERSHOOT, startOffset + dx));
  };

  const onTouchEnd = () => {
    if (axis === 'x') {
      suppressNextClick = true;
      dragging.value = false;
      if (offset.value < -unref(actionsWidth) * OPEN_RATIO) open();
      else close();
    }
    axis = null;
  };

  // Com as ações abertas (ou logo após arrastar), tocar no cartão só fecha.
  const onClickCapture = event => {
    if (event.target.closest?.('[data-swipe-actions]')) return;
    if (!suppressNextClick && !isOpen.value) return;
    suppressNextClick = false;
    event.preventDefault();
    event.stopPropagation();
    close();
  };

  return {
    offset,
    dragging,
    isOpen,
    close,
    onTouchStart,
    onTouchMove,
    onTouchEnd,
    onClickCapture,
  };
}
